import {
  CADASTRO_PessoaDataType,
  CONSUMO_PessoaDataType,
  EDICAO_PessoaDataType,
  FORM_PessoaDataType,
} from "@/types/membros"

/**
 * Verifica se uma data no formato DD/MM/YYYY é uma data real e não está no futuro.
 */
export function isValidBirthDate(value?: string): boolean {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? "")
  if (!match) return false

  const [, dd, mm, yyyy] = match
  const day = Number(dd)
  const month = Number(mm)
  const year = Number(yyyy)

  const date = new Date(year, month - 1, day)
  const isRealDate =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day

  return isRealDate && date <= new Date()
}

export function isValidCPF(value?: string): boolean {
  const cpf = (value ?? "").replace(/\D/g, "")
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false

  const digito = (len: number) => {
    let soma = 0
    for (let i = 0; i < len; i++) soma += Number(cpf[i]) * (len + 1 - i)
    const resto = (soma * 10) % 11
    return resto === 10 ? 0 : resto
  }
  return digito(9) === Number(cpf[9]) && digito(10) === Number(cpf[10])
}

export function isValidEmail(value?: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value ?? "").trim())
}

export function hasDigits(value: string | undefined, length: number): boolean {
  return (value ?? "").replace(/\D/g, "").length === length
}

/**
 * Mapeia os dados do formulário para o formato exigido na requisição de Cadastro.
 * @param formData - Dados do formulário (FORM_PessoaDataType).
 * @returns - Dados no formato CADASTRO_PessoaDataType.
 */
export function parseFormToCadastro(formData: FORM_PessoaDataType): CADASTRO_PessoaDataType {
  if (!formData.emp_id || !formData.data_nascimento || !formData.sexo || !formData.cep) {
    throw new Error(
      "Campos obrigatórios para cadastro (emp_id, data_nascimento, sexo, cep) estão faltando."
    )
  }

  return {
    emp_id: formData.emp_id,
    originador_id: formData.originador_id,
    cpf: formData.cpf,
    nome_completo: formData.nome_completo,
    nome_certificado: formData.nome_certificado,
    nome_cracha: formData.nome_cracha,
    data_nascimento: formData.data_nascimento,
    sexo: formData.sexo,
    chocolate_preferido: formData.chocolate_preferido,
    cep: formData.cep,
    telefone: formData.telefone,
    email: formData.email,
    cargo: formData.cargo,
    perfil: formData.perfil,
    departamento: formData.departamento,
    administrador: formData.administrador,
    status: formData.status,
  }
}

/**
 * Mapeia os dados retornados da API (CONSUMO) para o formato que preenche o formulário (FORM).
 * @param apiData - Dados da API (CONSUMO_PessoaDataType).
 * @returns - Dados no formato FORM_PessoaDataType.
 */
export function parseConsumoToForm(
  apiData: CONSUMO_PessoaDataType,
  originadorId: number
): FORM_PessoaDataType {
  // Função interna para formatar a data de YYYY-MM-DD para DD/MM/YYYY
  const formatarDataParaExibicao = (dataISO: string | null | undefined) => {
    if (!dataISO) return ""

    // Pega apenas a parte da data (YYYY-MM-DD) ignorando o tempo/Z
    const dataApenas = dataISO.split("T")[0]
    const [ano, mes, dia] = dataApenas.split("-")

    if (!ano || !mes || !dia) return ""

    return `${dia}/${mes}/${ano}`
  }

  return {
    pes_id: apiData.pes_id,
    originador_id: originadorId,

    // Campos mapeados conforme o objeto da API
    chocolate_preferido: apiData.pes_chocolate_preferido || "",
    data_nascimento: formatarDataParaExibicao(apiData.pes_nascimento),
    cep: apiData.pes_cep || "",

    // Tratamento de telefone (removendo 55 se necessário ou mantendo vazio)
    telefone: apiData.pes_telefone || "",

    email: apiData.pes_email,
    cargo: apiData.cargo,
    perfil: apiData.perfil,
    departamento: apiData.departamento,

    // Nomes de campos do formulário (Sincronize com os 'name' do MemberFormFields)
    nome_completo: apiData.pes_nome,
    nome_certificado: apiData.pes_nome_certificado,
    nome_cracha: apiData.pes_nome_cracha,
    cpf: apiData.pes_cpf,
    sexo: apiData.pes_sexo, // Adicionado caso falte
    administrador: !!apiData.admin,
    status: !!apiData.pes_status,
  }
}

/**
 * Mapeia os dados do formulário para o formato exigido na requisição de Edição.
 * @param formData - Dados do formulário (FORM_PessoaDataType).
 * @returns - Dados no formato EDICAO_PessoaDataType.
 */
export function parseFormToEdicao(formData: FORM_PessoaDataType): EDICAO_PessoaDataType {
  if (!formData.pes_id) {
    throw new Error("ID da pessoa (pes_id) é obrigatório para a edição.")
  }
  return {
    originador_id: formData.originador_id,
    pes_id: formData.pes_id,
    pes_nome: formData.nome_completo,
    pes_nome_certificado: formData.nome_certificado,
    pes_nome_cracha: formData.nome_cracha,
    chocolate_preferido: formData.chocolate_preferido,
    telefone: formData.telefone,
    email: formData.email,
    cargo: formData.cargo,
    perfil: formData.perfil,
    departamento: formData.departamento,
    admin: formData.administrador,
    status: formData.status,
    ...(formData.cpf && { cpf: formData.cpf }),
    ...(formData.data_nascimento && { data_nascimento: formData.data_nascimento }),
    ...(formData.sexo && { sexo: formData.sexo }),
    ...(formData.cep && { cep: formData.cep }),
  }
}
